import React, { useState, useEffect, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { notification } from 'antd'
import FileUpload from './FileUpload'
import { useCSVProcessor } from './hooks/useCSVProcessor'

const ERROR_MESSAGE =
  'An unexpected error occurred while processing your CSV file. This could be due to file corruption or an invalid format. Please check your file or contact support if the issue persists.'

const FileUploadContainer = ({
  onFileUpload,
  disabled,
  onUploadStart,
  requiredColumns,
  fieldsToOverride,
  fileName: initialFileName,
  fileValidation: initialFileValidation,
}) => {
  const {
    isProcessing,
    progress,
    timeRemaining,
    processCSV,
    reset,
    formatTimeRemaining,
    fileName: processorFileName,
    setFileName,
  } = useCSVProcessor()
  const [displayProgress, setDisplayProgress] = useState(0)
  const [showProgress, setShowProgress] = useState(false)
  const [fileValidation, setFileValidation] = useState(
    initialFileValidation || {
      missingColumns: [],
      extraColumns: [],
      duplicateColumns: [],
      invalidCommaRows: [],
      isValid: true,
    },
  )
  // Add state to control when to show validation messages
  const [showValidation, setShowValidation] = useState(
    !!initialFileValidation?.isValidationComplete ||
      !!initialFileValidation?.missingColumns?.length ||
      !!initialFileValidation?.extraColumns?.length ||
      !!initialFileValidation?.duplicateColumns?.length ||
      !!initialFileValidation?.invalidCommaRows?.length,
  )
  // New state for the validation details modal
  const [isValidationModalVisible, setIsValidationModalVisible] = useState(false)
  // State to track which validation sections are expanded (all collapsed by default)
  const [expandedSections, setExpandedSections] = useState({
    commaErrors: false,
    duplicateColumns: false,
    missingColumns: false,
    extraColumns: false,
  })
  // Store the last processed CSV result for revalidation
  const lastCsvResultRef = useRef(null)

  const animationRef = useRef(null)
  const lastProgressRef = useRef(displayProgress)
  const lastProgressUpdateTimeRef = useRef(Date.now())
  const simulatedProgressRef = useRef(null)
  // Store previous requiredColumns for comparison
  const prevRequiredColumnsRef = useRef(requiredColumns)

  // Determine if fields have been selected
  const hasFieldsSelected = Array.isArray(fieldsToOverride) && fieldsToOverride.length > 0

  // Toggle a validation section's expanded state
  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }))
  }

  // Initialize the CSV processor with the initial file name
  useEffect(() => {
    if (initialFileName && !processorFileName) {
      setFileName(initialFileName)
    }
  }, [initialFileName, processorFileName, setFileName])

  // Update fileValidation when parent changes initialFileValidation
  useEffect(() => {
    if (initialFileValidation) {
      setFileValidation(initialFileValidation)

      // Show validation for both warnings (extra columns) and errors
      if (!initialFileValidation.isValid || initialFileValidation.extraColumns?.length > 0) {
        setShowValidation(true)
      }
    }
  }, [initialFileValidation])

  const handleFileValidation = useCallback(
    (csvResult) => {
      // Build a comprehensive validation object
      const validation = {
        missingColumns: [],
        extraColumns: [],
        duplicateColumns: csvResult.duplicateColumns || [],
        invalidCommaRows: csvResult.invalidCommaRows || [],
        isValid: true,
        isValidationComplete: true,
      }

      // Check for invalid comma counts
      if (validation.invalidCommaRows && validation.invalidCommaRows.length > 0) {
        validation.isValid = false
      }

      // Check for duplicate columns
      if (validation.duplicateColumns && validation.duplicateColumns.length > 0) {
        validation.isValid = false
      }

      // If we have a valid structure, validate required columns
      if (csvResult.headers) {
        const headerSet = csvResult.headers
        validation.missingColumns = requiredColumns.filter((field) => !headerSet.has(field))
        validation.extraColumns = Array.from(headerSet).filter((header) => !requiredColumns.includes(header))

        if (validation.missingColumns.length > 0) {
          validation.isValid = false
        }
      }

      return validation
    },
    [requiredColumns],
  )

  // Modify the validation effect to handle navigation persistence
  useEffect(() => {
    // Preserve CSV result between steps using initialFileValidation
    if (initialFileValidation?.isValidationComplete && !lastCsvResultRef.current) {
      lastCsvResultRef.current = {
        headers: new Set(initialFileValidation.requiredColumns),
        rows: [], // Actual rows should come from parent state
        duplicateColumns: initialFileValidation.duplicateColumns,
        invalidCommaRows: initialFileValidation.invalidCommaRows,
      }
    }

    // Rest of existing validation effect logic...
  }, [requiredColumns, processorFileName, onFileUpload, handleFileValidation, initialFileValidation])

  // Re-validate when fieldsToOverride changes and we have a file loaded
  useEffect(() => {
    // Skip if no file is uploaded or no CSV result
    if (!processorFileName || !lastCsvResultRef.current) {
      prevRequiredColumnsRef.current = requiredColumns
      return
    }

    // Compare current requiredColumns with previous value
    const prevRequired = prevRequiredColumnsRef.current || []
    const currRequired = requiredColumns || []

    // Debug column comparison
    const columnsChanged = !(
      prevRequired.length === currRequired.length &&
      prevRequired.every((col) => currRequired.includes(col)) &&
      currRequired.every((col) => prevRequired.includes(col))
    )

    if (columnsChanged) {
      const newValidation = handleFileValidation(lastCsvResultRef.current)

      // Pass updated validation to parent
      onFileUpload(newValidation.isValid ? lastCsvResultRef.current.rows : [], {
        fileName: processorFileName,
        headers: lastCsvResultRef.current.headers,
        totalRows: newValidation.isValid ? lastCsvResultRef.current.rows.length : 0,
        validation: newValidation,
      })

      // Update local validation state
      setFileValidation(newValidation)
      // Show validation for both warnings (extra columns) and errors
      setShowValidation(!!newValidation)
    }

    // Update previous columns reference
    prevRequiredColumnsRef.current = requiredColumns
  }, [requiredColumns, processorFileName, onFileUpload, handleFileValidation])

  // Log progress changes to help with debugging
  useEffect(() => {
    if (progress > 0) {
      const now = Date.now()
      lastProgressUpdateTimeRef.current = now

      // Clear any simulated progress when we get a real progress update
      if (simulatedProgressRef.current) {
        clearInterval(simulatedProgressRef.current)
        simulatedProgressRef.current = null
      }

      // Only hide validation during processing, not if we have extra columns
      if (progress !== 100) {
        // Don't hide validation if we have extra columns
        if (!(fileValidation?.extraColumns?.length > 0)) {
          setShowValidation(false)
        }
      }
    }
  }, [progress, isProcessing, fileValidation])

  // Simulate intermediate progress for large files when progress stalls
  useEffect(() => {
    // Only start simulating if we're processing and progress is > 0 but < 100
    if (isProcessing && progress > 0 && progress < 100) {
      // Wait a bit to see if we get a real progress update
      const stallDetectionTimeout = setTimeout(() => {
        const now = Date.now()
        const timeSinceLastUpdate = now - lastProgressUpdateTimeRef.current

        // If it's been more than 1 second since the last real progress update, start simulating
        if (timeSinceLastUpdate > 1000 && !simulatedProgressRef.current) {
          // Calculate how much progress to simulate based on current progress
          // The closer we are to 100%, the slower we should simulate
          const getSimulationIncrement = (currentProgress) => {
            if (currentProgress < 10) {
              return 0.2
            } // Faster at the beginning
            if (currentProgress < 50) {
              return 0.1
            } // Medium speed in the middle
            return 0.05 // Slower near the end
          }

          // Start a simulation interval that slowly increases progress
          simulatedProgressRef.current = setInterval(() => {
            const currentProgress = lastProgressRef.current
            const increment = getSimulationIncrement(currentProgress)

            // Don't go beyond 95% with simulation (leave room for real completion)
            if (currentProgress < 95) {
              const simulatedProgress = Math.min(95, currentProgress + increment)
              setDisplayProgress(simulatedProgress)
              lastProgressRef.current = simulatedProgress
            } else {
              // Stop simulating if we reach 95%
              clearInterval(simulatedProgressRef.current)
              simulatedProgressRef.current = null
            }
          }, 200) // Update every 200ms for smooth animation
        }
      }, 1000) // Wait 1 second before deciding progress is stalled

      return () => {
        clearTimeout(stallDetectionTimeout)
        if (simulatedProgressRef.current) {
          clearInterval(simulatedProgressRef.current)
          simulatedProgressRef.current = null
        }
      }
    }

    // If processing completes or stops, clear any simulation
    if (!isProcessing || progress >= 100) {
      if (simulatedProgressRef.current) {
        clearInterval(simulatedProgressRef.current)
        simulatedProgressRef.current = null
      }
    }
  }, [isProcessing, progress])

  // Smooth progress animation for real progress updates
  useEffect(() => {
    // Store the current display progress value in the ref
    lastProgressRef.current = displayProgress

    if (isProcessing || progress === 100) {
      setShowProgress(true)

      // Cancel any existing animation
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }

      const startTime = Date.now()
      const startProgress = lastProgressRef.current
      const targetProgress = progress

      // Calculate an appropriate duration based on the progress delta
      // For small changes (< 5%), use a shorter duration
      // For larger changes, use a longer duration to ensure smoothness
      const progressDelta = Math.abs(targetProgress - startProgress)
      let duration = 600 // Base duration of 0.6 seconds

      if (progressDelta > 20) {
        // For large jumps, use longer duration to make it smoother
        duration = 800
      } else if (progressDelta > 5) {
        // For medium jumps
        duration = 600
      }

      // If we're completing (progress = 100), always use a consistent duration
      if (progress === 100) {
        duration = 600 // Revert back to 600ms
      }

      const animate = () => {
        const currentTime = Date.now()
        const elapsed = currentTime - startTime

        if (elapsed < duration) {
          // Use easeOutQuad easing function for smoother animation
          const t = elapsed / duration
          const easeT = t * (2 - t) // easeOutQuad formula
          const nextProgress = startProgress + (targetProgress - startProgress) * easeT

          setDisplayProgress(nextProgress)
          animationRef.current = requestAnimationFrame(animate)
        } else {
          setDisplayProgress(targetProgress)
          animationRef.current = null

          // Show validation only when animation completes and we're at 100%
          if (targetProgress === 100) {
            // Only set showValidation if it's not already true (for extra columns)
            setShowValidation((prevState) => {
              const newState = prevState || true
              return newState
            })
          }
        }
      }

      animationRef.current = requestAnimationFrame(animate)

      // Cleanup function to cancel animation when component unmounts or effect re-runs
      return () => {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current)
        }
      }
    }
    // We intentionally exclude displayProgress from the dependency array
    // to prevent an infinite loop, as this effect updates displayProgress.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProcessing, progress])

  const handleFileUploadStart = async (file, metadata) => {
    if (metadata?.action === 'remove') {
      // Preserve validation state when navigating
      if (initialFileValidation) {
        setFileValidation(initialFileValidation)
        setShowValidation(true)
      } else {
        setShowProgress(false)
        setDisplayProgress(0)
        setFileValidation({
          missingColumns: [],
          extraColumns: [],
          duplicateColumns: [],
          invalidCommaRows: [],
          isValid: true,
        })
        setShowValidation(false)
        setIsValidationModalVisible(false)
        reset()
        lastCsvResultRef.current = null
        onFileUpload([], { fileName: '', headers: new Set(), totalRows: 0 })
        if (onUploadStart) {
          onUploadStart(false)
        }
      }
      return
    }

    if (!file) {
      return
    }

    if (onUploadStart) {
      onUploadStart(true)
    }

    // Hide validation while processing
    setShowValidation(false)

    lastProgressUpdateTimeRef.current = Date.now()
    let result = null
    try {
      result = await processCSV(file)
    } catch (error) {
      // Display user-friendly error message
      notification.error({
        message: 'CSV Processing Error',
        description: ERROR_MESSAGE,
        duration: 10,
        className: 'csv-processing-error-notification',
        placement: 'topRight',
      })

      setShowProgress(false)
      setDisplayProgress(0)
      setFileValidation({
        missingColumns: [],
        extraColumns: [],
        duplicateColumns: [],
        invalidCommaRows: [],
        isValid: true,
      })
      setShowValidation(false)
      reset()
      lastCsvResultRef.current = null
      if (onUploadStart) {
        onUploadStart(false)
      }
      return false // Explicitly return false to prevent default upload
    }
    if (!result) {
      return false
    }

    // Store the result for potential revalidation
    lastCsvResultRef.current = result

    const validation = handleFileValidation(result)

    // Always pass the validation state to parent
    onFileUpload(validation.isValid ? result.rows : [], {
      fileName: file.name,
      headers: result.headers || new Set(),
      totalRows: validation.isValid ? result.rows.length : 0,
      validation,
    })

    // Show validation immediately if we have extra columns, don't wait for animation
    if (validation.extraColumns?.length > 0) {
      setShowValidation(true)
      // Make sure to update local fileValidation state to reflect extra columns
      setFileValidation(validation)
    }

    if (onUploadStart) {
      onUploadStart(false)
    }

    return false // Explicitly return false to prevent default upload
  }

  const handleUploadChange = (info) => {
    if (info.file.status === 'uploading') {
      setShowProgress(true)
      setShowValidation(false)
      setIsValidationModalVisible(false)
    }
  }

  const handleRemoveFile = (e) => {
    if (e) {
      e.stopPropagation()
    }

    // First reset the CSV processor
    reset()

    // Then reset our local state
    setDisplayProgress(0)
    setShowProgress(false)
    setFileValidation({
      missingColumns: [],
      extraColumns: [],
      duplicateColumns: [],
      invalidCommaRows: [],
      isValid: true,
    })
    setShowValidation(false)
    setIsValidationModalVisible(false)
    lastCsvResultRef.current = null

    // Notify parent component
    onFileUpload([], {
      fileName: '',
      headers: new Set(),
      totalRows: 0,
      validation: {
        missingColumns: [],
        extraColumns: [],
        duplicateColumns: [],
        invalidCommaRows: [],
        isValid: true,
      },
    })

    if (onUploadStart) {
      onUploadStart(false)
    }
  }

  // Handle opening the validation modal
  const handleOpenValidationModal = () => {
    setIsValidationModalVisible(true)
  }

  // Handle closing the validation modal
  const handleCloseValidationModal = () => {
    setIsValidationModalVisible(false)
  }

  return (
    <FileUpload
      onFileUpload={handleFileUploadStart}
      onUploadChange={handleUploadChange}
      isProcessing={isProcessing}
      progress={progress}
      displayProgress={displayProgress}
      showProgress={showProgress}
      timeRemaining={timeRemaining}
      formatTimeRemaining={formatTimeRemaining}
      disabled={disabled || !!processorFileName}
      fileName={processorFileName}
      fileValidation={fileValidation}
      showValidation={showValidation}
      onRemoveFile={handleRemoveFile}
      hasFieldsSelected={hasFieldsSelected}
      isValidationModalVisible={isValidationModalVisible}
      onOpenValidationModal={handleOpenValidationModal}
      onCloseValidationModal={handleCloseValidationModal}
      expandedSections={expandedSections}
      onToggleSection={toggleSection}
    />
  )
}

FileUploadContainer.propTypes = {
  onFileUpload: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  onUploadStart: PropTypes.func,
  requiredColumns: PropTypes.arrayOf(PropTypes.string).isRequired,
  fieldsToOverride: PropTypes.arrayOf(PropTypes.string).isRequired,
  fileName: PropTypes.string,
  fileValidation: PropTypes.shape({
    missingColumns: PropTypes.arrayOf(PropTypes.string),
    extraColumns: PropTypes.arrayOf(PropTypes.string),
    duplicateColumns: PropTypes.arrayOf(PropTypes.string),
    invalidCommaRows: PropTypes.arrayOf(
      PropTypes.shape({
        rowNumber: PropTypes.number,
        lineContent: PropTypes.string,
        expectedCommas: PropTypes.number,
        actualCommas: PropTypes.number,
      }),
    ),
    isValid: PropTypes.bool,
  }),
}

export default FileUploadContainer
